import {} from "@api";
import type { IDocument } from "@bbj-types/document";
import { documentTypesLabels } from "@bbj-types/document/constant";
import type { ObjectId } from "@bbj-types/utils";
import { IconButtonVariant } from "@inputs/button/IconButtonVariant";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Popover, Stack, Typography } from "@mui/material";
import { useLayoutEffect, useMemo, useState } from "react";
import { UploadFileFieldPopoverSelectableDocumentsItem } from "./item";

interface IDocumentGroup {
  title: string;
  documents: IDocument[];
}

export interface IUploadFileFieldPopoverSelectableDocumentsProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  documents: IDocument[];
  selectableDocuments: IDocument[];
  onSubmit: (selectedDocuments: Set<ObjectId>) => void;
  onClose: () => void;
}

export const UploadFileFieldPopoverSelectableDocuments = ({
  open,
  anchorEl,
  documents,
  selectableDocuments,
  onSubmit,
  onClose,
}: IUploadFileFieldPopoverSelectableDocumentsProps) => {
  const [selectedDocuments, setSelectedDocuments] = useState(new Set<ObjectId>());

  const groupedDocuments = useMemo(
    () =>
      Object.values(
        selectableDocuments.reduce<Record<string, IDocumentGroup>>((acc, curr) => {
          if (acc[curr.typeDoc]) {
            acc[curr.typeDoc].documents.push(curr);
          } else {
            acc[curr.typeDoc] = {
              title: documentTypesLabels[curr.typeDoc],
              documents: [curr],
            };
          }

          return acc;
        }, {})
      ).sort((a, b) => a.title.localeCompare(b.title)),
    [selectableDocuments]
  );

  const handleSubmit = () => {
    onSubmit(selectedDocuments);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useLayoutEffect(() => {
    if (open) {
      setSelectedDocuments(new Set(documents.map((doc) => doc._id)));
    }
  }, [open]);

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      slotProps={{ paper: { variant: "outlined", elevation: 0, sx: { borderRadius: 0, width: 304 } } }}
    >
      <Stack
        sx={{
          px: 2,
        }}
      >
        {groupedDocuments.map((documentGroup) => (
          <Stack
            key={documentGroup.title}
            sx={(theme) => ({ borderBottom: `solid 1px ${theme.palette.border.main}`, pb: 1 })}
          >
            <Typography variant="h3" sx={{ py: 2 }}>
              {documentGroup.title}
            </Typography>
            {documentGroup.documents.map((document) => (
              <UploadFileFieldPopoverSelectableDocumentsItem
                key={document._id}
                document={document}
                selectedDocuments={selectedDocuments}
                setSelectedDocuments={setSelectedDocuments}
              />
            ))}
          </Stack>
        ))}
        <Stack
          direction="row"
          sx={{
            justifyContent: "end",
            alignItems: "center",
            pt: 1,
            pb: 2,
            gap: 1,
          }}
        >
          <IconButtonVariant variant="elevated" onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButtonVariant>
          <IconButtonVariant variant="elevated" onClick={handleSubmit}>
            <CheckIcon color="success" />
          </IconButtonVariant>
        </Stack>
      </Stack>
    </Popover>
  );
};
